import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLine } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1800&q=80";

const stats = [
  { value: "9%", label: "Flat Dispatch Fee" },
  { value: "24/7", label: "Dispatcher Access" },
  { value: "91%", label: "Revenue You Keep" },
  { value: "0", label: "Forced Loads" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const imgX = useTransform(sx, [-0.5, 0.5], [-26, 26]);
  const imgY = useTransform(sy, [-0.5, 0.5], [-14, 14]);

  const onMouseMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Semi truck on the highway"
          style={{ x: imgX, y: imgY, scale: 1.12 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.85 }}
          transition={{ duration: 1.6, delay: 0.3 }}
          className="w-full h-full object-cover grayscale contrast-125"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/25 to-[#0B0B0B]/40" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(11,11,11,0.45)_100%)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-40 pb-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.35em] text-neutral-400 mb-6"
          data-testid="hero-eyebrow"
        >
          Purpose Haul Dispatch — Charlotte, NC
        </motion.p>

        <h1 className="font-display font-extrabold uppercase leading-[0.92] tracking-tighter text-[10.5vw] sm:text-[9.5vw] lg:text-[6.75rem]">
          <MaskedLine delay={0.35}>Driven</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="text-stroke">With</span> Purpose.
          </MaskedLine>
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-md text-neutral-300 text-base sm:text-lg leading-relaxed"
            data-testid="hero-subtext"
          >
            Dispatch support for owner-operators and small carriers ready to spend
            less time searching for freight — and more time keeping their trucks moving.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap gap-4"
          >
            <a
              href="#contact"
              data-testid="hero-become-carrier-button"
              className="group inline-flex items-center gap-3 bg-white text-black font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-neutral-300 transition-colors duration-300"
            >
              Become a Carrier
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#services"
              data-testid="hero-explore-services-button"
              className="inline-flex items-center gap-3 border border-white/40 text-white font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 hover:bg-white hover:text-black transition-colors duration-300"
            >
              Explore Services
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="mt-14 grid grid-cols-2 lg:grid-cols-4 border-t border-white/15"
          data-testid="hero-stats-strip"
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              className={`py-6 pr-6 ${i !== 0 ? "lg:border-l lg:border-white/15 lg:pl-6" : ""}`}
              data-testid={`hero-stat-${i}`}
            >
              <div className="font-display font-bold text-3xl sm:text-4xl">{s.value}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 z-10 hidden lg:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500"
      >
        Scroll <ArrowDown size={12} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
